/**
 * Read-only display of seed script dependencies in the API sequence view.
 *
 * @module FlowDesigner/api/SeedDependencyCard
 */

import React from 'react';
import { ArrowSquareOut, Plant } from '@phosphor-icons/react';
import { Text, TagBadge } from '@chatsdet/ui';
export type SeedDependencyInfo = {
  nodeId: string;
  seedWorkflowId: string;
  seedWorkflowName: string | null;
  snapshotVersion: number | null;
  onFailure: 'block' | 'warn';
};

const SeedDependencyCard: React.FC<{ dep: SeedDependencyInfo }> = ({ dep }) => {
  const versionLabel = dep.snapshotVersion === null ? 'Latest' : `v${dep.snapshotVersion}`;

  return (
    <div className="border rounded-lg bg-card shadow-sm px-3 py-2 flex items-center gap-2">
      <div className="p-1 rounded bg-teal-500/10">
        <Plant size={16} weight="duotone" className="text-teal-600" />
      </div>
      <Text size="sm" weight="medium" className="flex-1 truncate">
        {dep.seedWorkflowName || 'Unnamed seed'}
      </Text>
      <TagBadge name={versionLabel} color="gray" hideDot size="sm" />
      {dep.onFailure === 'warn' && (
        <Text size="xs" className="text-amber-500 whitespace-nowrap">
          Continue on failure
        </Text>
      )}
      <a
        href={`/flows/${dep.seedWorkflowId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-1 rounded hover:bg-muted flex-shrink-0"
        title="View seed flow"
      >
        <ArrowSquareOut size={14} className="text-muted-foreground" />
      </a>
    </div>
  );
};

type SeedDependencySectionProps = {
  dependencies: SeedDependencyInfo[];
};

export const SeedDependencySection: React.FC<SeedDependencySectionProps> = ({
  dependencies,
}) => {
  if (dependencies.length === 0) return null;

  return (
    <div className="space-y-2 mb-4">
      <div className="flex items-center gap-2">
        <Text size="sm" weight="semibold">Seed Dependencies</Text>
        <TagBadge name={dependencies.length} color="gray" hideDot size="sm" />
      </div>
      {dependencies.map((dep) => (
        <SeedDependencyCard key={dep.nodeId} dep={dep} />
      ))}
    </div>
  );
};
